import dayjs from "dayjs";
import { useContext, useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import TkPropertyMessageGet from "../../../utils/TkPropertyMessageGet";
import { ITkDurationPickerProps } from "../../../type/componentsType/TkDurationPicker";
import TkDatePicker from "../../atoms/TkDatePicker";
import TkInputWithText from "../TkInputWithText";
import "./_style.scss";
import { TkFormContext } from "../../../context/tkFormContext";

/**
 * TkDurationPicker 部品
 * 期間選択用のカスタムピッカー部品です。開始日と終了日を選択し、期間の年数を計算します。
 * @param {Object} props - コンポーネントのprops。
 * @param {boolean} props.showYears - 年数を表示するかどうかのフラグ。
 * @param {"floor" | "ceil" | "round"} props.round - 年数計算の方法 floor:切り捨て ceil:切り上げ round:四捨五入
 * @param {DurationPickerDate} props.start - 開始日を設定するためのプロパティ。
 * @param {DurationPickerDate} props.end - 終了日を設定するためのプロパティ。
 * @param {Function} props.onChange - 開始日と終了日の変更時に呼び出されるコールバック関数。
 * @return TkDurationPicker 期間選択部品
 */
const TkDurationPicker: React.FC<ITkDurationPickerProps> = (props) => {
  const {
    showYears = true,
    round = "ceil",
    start,
    end,
    onChange,
    onGetInputValue,
    minDate,
    maxDate,
    error,
    errorMsg,
  } = props;
  const [dateRange, setDateRange] = useState({
    startDate: start?.wantToSetValue || null,
    endDate: end?.wantToSetValue || null,
  });
  const [years, setYears] = useState<number | null>(null);
  const [errorMessages, setErrorMessages] = useState<string>("");
  const formValue = useRef<any>();
  const { formContext, setFormContext } = useContext(TkFormContext);

  useEffect(() => {
    const res = {
      startDate: start?.wantToSetValue,
      endDate: end?.wantToSetValue,
      years: years,
    };

    setDateRange({
      startDate: start?.wantToSetValue || null,
      endDate: end?.wantToSetValue || null,
    });
    start && end && onChange && onChange(res);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [start, end]);

  useEffect(() => {
    const getYears = (start: dayjs.Dayjs, end: dayjs.Dayjs) => {
      return {
        ceil: Math.ceil(end.diff(start, "year", true)),
        floor: Math.floor(end.diff(start, "year", true)),
        round: Math.round(end.diff(start, "year", true)),
        floorAdd: () => {
          let diff = end.diff(start, "year", true);
          if (
            diff === Math.floor(diff) &&
            !(
              start.format("MM/DD") === "02/29" &&
              end.format("MM/DD") === "02/28"
            )
          ) {
            ++diff;
          }
          return Math.ceil(diff);
        },
      }[round];
    };

    const { startDate, endDate } = dateRange;
    if (startDate && endDate) {
      const start = dayjs(startDate);
      const end = dayjs(endDate);
      const diffYears = getYears(start, end);
      if (start > end) {
        setErrorMessages(TkPropertyMessageGet("TKMSG_SCR0070E"));
        setFormContext({
          preventSubmit: {
            ...formContext?.preventSubmit,
            duration: true,
          },
        });
        setYears(null);
      } else {
        setErrorMessages("");
        setYears(diffYears);
        if (formContext?.preventSubmit?.duration)
          setFormContext({
            preventSubmit: {
              ...formContext?.preventSubmit,
              duration: false,
            },
          });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, round, setFormContext]);

  const handelChange =
    (type: "startDate" | "endDate" | "years") => (e: any) => {
      if (e.target.value === "Invalid Date") return;

      const newValue: any = {
        ...dateRange,
        [type]: e.target.value,
      };

      setDateRange(newValue);

      let changeValue: any = null;

      if (newValue.startDate && newValue.endDate) {
        changeValue = newValue;
      }

      const res = {
        ...formValue.current,
        [type]: e.target.value,
        target: {
          value: changeValue,
        },
      };

      formValue.current = res;

      onChange && onChange(res);
      onGetInputValue && onGetInputValue(res);
    };

  return (
    <Box className="tkDurationPicker">
      <Box className="date">
        <Box className="start">
          開始
          <TkDatePicker
            {...start}
            wantToSetValue={start?.wantToSetValue || null}
            onChange={handelChange("startDate")}
            minDate={minDate}
            maxDate={maxDate}
            error={errorMessages !== "" || error}
          />
        </Box>
        <Box className="end">
          終了
          <TkDatePicker
            {...end}
            wantToSetValue={end?.wantToSetValue || null}
            onChange={handelChange("endDate")}
            minDate={minDate}
            maxDate={maxDate}
            error={errorMessages !== "" || error}
            errorMessage={errorMessages || errorMsg}
          />
        </Box>
      </Box>
      {showYears && (
        <TkInputWithText
          disabled
          size="medium"
          innerText="年"
          wantToSetValue={years}
          onChange={handelChange("years")}
          inputProps={{
            style: { textAlign: "right" },
          }}
        />
      )}
    </Box>
  );
};

export default TkDurationPicker;
